import { useState, useEffect } from 'react';
import User from '../models/user';
import { getAllWhyellowAndAdminUsers } from '../user/user.service';

import { getAllOfficeUsers, getAllOfficeManagersForUser, getUserForOfficeManagerByEmail } from '../user/office-user.service';
import type OfficeManagerUser from '../models/officeManagerUser';
import { useOfficeInformation } from '../../offices/hooks/offices.hook';
import type AppUser from '../../check-in-employees/models/app-user';
import { useKeycloak } from '../keycloak/KeycloakProvider';

interface BasicUserState {
  readonly initialized: boolean;
  readonly error?: boolean;
}

interface AppUserState extends BasicUserState {
  readonly user?: AppUser;
}

interface UsersState extends BasicUserState {
  readonly users?: User[];
}

interface OfficeManagerUserState extends BasicUserState {
  readonly users?: OfficeManagerUser[];
}

export const useAllWhyellowAndAdminUsers = (): UsersState => {
  const [state, setState] = useState<UsersState>({
    initialized: false,
  });

  const keycloak = useKeycloak();

  useEffect(() => {
    const getUsers = async (): Promise<void> => {
      try {
        const users = await getAllWhyellowAndAdminUsers(keycloak);
        setState({ initialized: true, users });
      } catch (error) {
        setState({ initialized: false, error: true });
      }
    };

    if (!state.initialized) {
      getUsers();
    }
  }, [setState, state.initialized, keycloak]);

  return state;
};

interface Props {
  readonly officeId?: string;
  readonly email?: string;
}

export const useAllOfficeUsers = ({ officeId }: Props): UsersState => {
  const [state, setState] = useState<UsersState>({
    initialized: false,
  });
  const keycloak = useKeycloak();
  const { office } = useOfficeInformation({ id: officeId });

  useEffect(() => {
    const getUsers = async (): Promise<void> => {
      try {
        if (officeId === undefined) {
          return;
        }

        const usersData = await getAllOfficeUsers(officeId, keycloak);
        const users: User[] = [];

        if (usersData != null) {
          usersData.forEach((userData) => {
            users.push(Object.assign(new User(), userData));
          });
        }

        setState({ initialized: true, users });
      } catch (error) {
        setState({ initialized: false, error: true });
      }
    };

    if (!state.initialized) {
      getUsers();
    }
  }, [officeId, office, setState, state.initialized, keycloak]);

  return state;
};

export const useOfficeManagersForUser = (): OfficeManagerUserState => {
  const [state, setState] = useState<OfficeManagerUserState>({
    initialized: false,
  });
  const keycloak = useKeycloak();

  useEffect(() => {
    const getOfficeManagers = async (): Promise<void> => {
      try {
        const officeManagers = (await getAllOfficeManagersForUser(keycloak)) ?? [];
        setState({ initialized: true, users: officeManagers });
      } catch (error) {
        setState({ initialized: false, error: true });
      }
    };

    if (!state.initialized) {
      getOfficeManagers();
    }
  }, [setState, state.initialized, keycloak]);
  return state;
};

export const useUserForPermittedUserEmail = ({ email }: Props): AppUserState => {
  const [state, setState] = useState<AppUserState>({
    initialized: false,
  });
  const keycloak = useKeycloak();

  useEffect(() => {
    const getUser = async (): Promise<void> => {
      try {
        if (email === undefined) {
          return;
        }

        const user = await getUserForOfficeManagerByEmail(keycloak, email);

        if (user == null) {
          throw new Error('User not found');
        }

        setState({ initialized: true, user });
      } catch (error) {
        setState({ initialized: false, error: true });
      }
    };

    getUser();
  }, [setState, keycloak, email]);
  return state;
};
